import { useEmbed } from '@components/utils/contexts/EmbedContext';
import { IconButtonProps } from '@lib/uikit';
import localeDirection from '@utils/locale/direction';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { useProgressDispatchContext } from '../contexts/ProgressContext';
import { SanitizedStoryPage } from '../types';

export type UseVideoPlayerProps = {
    storyPage: SanitizedStoryPage;
    autoPlay?: boolean;
    isCurrent: boolean;
};

export type UseVideoPlayerReturn = {
    vid: React.MutableRefObject<HTMLVideoElement | null>;
    replayVisible: boolean;
    playVisible: boolean;
    isLoaded: boolean;
    videoHasAudio: boolean;
    isMuted: boolean;
    isPaused: boolean;
    playVideo: () => void;
    pauseVideo: () => void;
    restartVideo: () => void;
    onCanPlayThrough: () => void;
    onEnded: () => void;
    buttons: IconButtonProps[];
    isRtl: boolean;
};

const useVideoPlayer = ({
    storyPage,
    isCurrent,
}: UseVideoPlayerProps): UseVideoPlayerReturn => {
    const router = useRouter();
    const [isPaused, setIsPaused] = useState<boolean>(true);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const dispatch = useProgressDispatchContext();
    const vid = useRef<HTMLVideoElement>(null);
    const [hasAudio, setHasAudio] = useState<boolean>(true);
    const [replayVisible, setReplayVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const { isEmbed, isLoading } = useEmbed();

    const playVisible = useMemo(
        () => Boolean(isPaused && !replayVisible),
        [isPaused, replayVisible]
    );

    useEffect(() => {
        vid.current?.load();
    }, []);

    const pauseVideo = useCallback(
        (pauseProgress = true) => {
            vid.current?.pause();
            setIsPaused(true);
            if (pauseProgress) dispatch({ type: 'PAUSE' });
        },
        [dispatch]
    );

    const muteVideo = useCallback(() => {
        setIsMuted(true);
    }, []);

    const unmuteVideo = useCallback(() => {
        setIsMuted(false);
    }, []);

    const playVideo = useCallback(async () => {
        if (vid.current) {
            try {
                const currentTime = vid.current.currentTime;
                await vid.current.play();
                setIsPaused(false);
                setReplayVisible(false);

                if (currentTime > 0) {
                    dispatch({ type: 'RESUME' });
                } else {
                    dispatch({
                        type: 'START',
                        payload: {
                            duration: vid.current.duration,
                            isInfinite: storyPage.looping,
                        },
                    });
                }
            } catch (error) {
                console.warn('[Play failed] :', error);
                pauseVideo();
            }
        }
    }, [storyPage, dispatch, pauseVideo]);

    const resetVideo = useCallback(() => {
        if (vid.current) {
            vid.current.currentTime = 0;
            if (isSafari) vid.current.load();
            dispatch({ type: 'RESET' });
        }
    }, [dispatch]);

    const restartVideo = useCallback(() => {
        resetVideo();
        playVideo();
        dispatch({ type: 'RESTART' });
    }, [dispatch, playVideo, resetVideo]);

    useEffect(() => {
        if (vid.current) {
            if (!isCurrent && !vid.current.paused) {
                pauseVideo(false);
            }

            if (isCurrent && isLoaded) {
                restartVideo();
            }
        }
    }, [isCurrent, isLoaded, pauseVideo, restartVideo]);

    const onCanPlayThrough = () => {
        setIsLoaded(true);
        checkAudioTracks();
    };

    const onEnded = () => {
        if (storyPage.looping) {
            restartVideo();
        } else {
            dispatch({ type: 'NEXT_PAGE' });
        }
    };

    const eventProcessor = useCallback(
        (event: MessageEvent) => {
            if (!event.data) throw new Error('Invalid message payload');
            switch (event.data.action) {
                case 'pauseVideo':
                    pauseVideo();
                    break;
                case 'muteVideo':
                    muteVideo();
                    break;
                default:
                    throw new Error('Invalid message action');
            }
        },
        [pauseVideo, muteVideo]
    );

    useEffect(() => {
        if (!isLoading && isEmbed) {
            // Attach event listener
            window.addEventListener('message', eventProcessor);
            return () => window.removeEventListener('message', eventProcessor);
        }
    }, [isEmbed, isLoading, eventProcessor]);

    const checkAudioTracks = useCallback(() => {
        if (vid.current) {
            const videoElement = vid.current;
            //@ts-expect-error
            const audioTracks = videoElement.audioTracks;
            // For browsers that support audioTracks
            if (audioTracks && audioTracks.length > 0) {
                setHasAudio(true);
            } else {
                // For browsers that do not support audioTracks API, we can check if any audio tracks are present in the video element
                const audioEnabled =
                    //@ts-expect-error
                    videoElement.mozHasAudio ||
                    //@ts-expect-error
                    Boolean(videoElement.webkitAudioDecodedByteCount);

                setHasAudio(audioEnabled);
            }
        }
    }, [vid, setHasAudio]);

    useEffect(() => {
        if (!vid.current) return;
        const videoElement = vid.current;
        // Attach the event listener to each event in the list
        videoElement.onplay = checkAudioTracks;

        return () => {
            videoElement.onplay = null;
        };
    }, [vid, checkAudioTracks]);

    const buttons: IconButtonProps[] = useMemo(() => {
        const _buttons: IconButtonProps[] = [];

        if (hasAudio) {
            _buttons.push({
                ariaLabel: isMuted ? 'Unmute Video' : 'Mute Video',
                icon: !isMuted ? 'volume_up' : 'volume_off',
                onClick: () => {
                    if (isMuted) {
                        unmuteVideo();
                    } else {
                        vid.current!.muted = true;
                        muteVideo();
                    }
                },
            });
        }

        _buttons.push({
            ariaLabel: isPaused ? 'Play Video' : 'Pause Video',
            icon: isPaused ? 'play_arrow' : 'pause',
            onClick: () => (isPaused ? playVideo() : pauseVideo()),
        });

        return _buttons;
    }, [
        isPaused,
        isMuted,
        hasAudio,
        muteVideo,
        unmuteVideo,
        playVideo,
        pauseVideo,
    ]);

    const isRtl = localeDirection(router.locale) === 'rtl';

    return {
        vid,
        isLoaded,
        videoHasAudio: hasAudio,
        playVisible,
        isMuted,
        isPaused,
        replayVisible,
        buttons,
        isRtl,
        playVideo,
        pauseVideo,
        restartVideo,
        onCanPlayThrough,
        onEnded,
    };
};

export default useVideoPlayer;
